import { api } from '@/shared/api/client';
import { carTypeLabel, statusFromInt } from '@/shared/data';
import type { IntakeEntry, QueueCar } from '@/shared/types';

/* ---- Server DTO (PascalCase — backend convention) ---- */

export interface CwServiceDto {
  Id: string;
  Name: string;
  PriceSedan: number;
  PriceJeep: number;
  PriceTruck: number;
  PriceVan: number;
  IsActive: boolean;
  SortOrder: number;
}

export interface CwWasherDto {
  Id: string;
  Name: string;
  Phone?: string | null;
  IsActive: boolean;
  ActiveCarCount: number;
  TotalWashedCars: number;
}

export interface CwCustomerOrderDto {
  Id: string;
  Plate: string;
  CarType: number;
  PaidAt?: string | null;
  Total: number;
  Services: string[];
}

export interface CwCustomerDto {
  Id: string;
  Plate: string;
  Name?: string | null;
  Phone?: string | null;
  CarType: number;
  Visits: number;
  Note?: string | null;
  TotalSpent: number;
  LastVisitAt?: string | null;
}

export interface CwOrderLineDto {
  Id: string;
  ServiceId?: string | null;
  ServiceName: string;
  Price: number;
}

export interface CwOrderDto {
  Id: string;
  Plate: string;
  CarType: number;
  Status: number;
  CustomerId?: string | null;
  CustomerName?: string | null;
  CustomerPhone?: string | null;
  CustomerVisits: number;
  Washers: { Id: string; Name: string }[];
  WasherName?: string | null;
  EnteredAt: string;
  Subtotal: number;
  DiscountAmount: number;
  Total: number;
  Lines: CwOrderLineDto[];
}

export interface CwPayRequest {
  DiscountType: number;
  DiscountValue: number;
  PaymentMethod: number;
  EbarimtType: number;
  EbarimtRd?: string | null;
}

export interface CwTodayStatsDto {
  Cars: number;
  Revenue: number;
  AvgTicket: number;
}

export interface CwReportDto {
  Today: CwTodayStatsDto;
  Week: { Day: string; Date: string; Revenue: number; Cars: number }[];
  TopServices: { Name: string; Count: number; Revenue: number }[];
  WasherPerf: { Name: string; Cars: number; Revenue: number }[];
  Hourly: { Hour: number; Cars: number }[];
}

export interface CwVehicleInfoDto {
  Found: boolean;
  PlateNumber: string;
  MarkName?: string | null;
  ModelName?: string | null;
  ColorName?: string | null;
  ManCount?: number | null;
  Length?: number | null;
  Width?: number | null;
  WheelPosition?: string | null;
}

export interface CwShiftDto {
  Id: string;
  Status: number;
  OpenedAt: string;
  ClosedAt?: string | null;
  OpenedBy?: string | null;
  ClosedBy?: string | null;
  Cars: number;
  Revenue: number;
}

export interface CwShiftCurrentDto {
  Shift?: CwShiftDto | null;
  Cars: number;
  Revenue: number;
  AvgTicket: number;
  LastClosed?: CwShiftDto | null;
}

export interface CwShiftPaymentStatDto {
  Method: number;
  Cars: number;
  Amount: number;
}

export interface CwQpayStatusDto {
  HasBankAccount: boolean;
  ConnectionId?: string | null;
  Status?: number | null;
  BankName?: string | null;
  BankAccountNo?: string | null;
  RequestedAt?: string | null;
  ConfirmedAt?: string | null;
}

export interface CwQpayStartResponse {
  WireId: string;
  QrImage?: string | null;
  QrText?: string | null;
  Amount: number;
}

export interface CwQpayCheckResponse {
  Status: string;
  Paid: boolean;
  Order?: CwOrderDto | null;
}

/* ---- Subscription багц ---- */

export interface TenantPlanDto {
  Id: string;
  Name: string;
  /* SubscriptionPlan int: 0 FreeTrial, 1 Starter(Стандарт), 2 Gold(Про) */
  Plan: number;
  SubscriptionStatus: number;
  TrialEndsOn?: string | null;
  PlanExpiresOn?: string | null;
}

export interface InvoiceDto {
  Id: string;
  InvoiceNo: string;
  TenantId: string;
  Kind: number;
  Plan: number;
  PlanLabel: string;
  Amount: number;
  Currency: string;
  /* InvoiceStatus int: 0 Pending, 1 Paid, 2 Cancelled */
  Status: number;
  PeriodMonths: number;
  CreatedOn: string;
  PaidOn?: string | null;
}

export interface BankDto {
  Id: string;
  BankCode: string;
  BankName: string;
  DisplayOrder: number;
}

export interface BankAccountDto {
  Id: string;
  FKBankId: string;
  BankCode?: string | null;
  BankName?: string | null;
  BankAccountNo: string;
  BankAccountName?: string | null;
  IsActive: boolean;
  IsDefault: boolean;
}

export interface BankAccountLookupResponse {
  AccountIban: string;
  AccountName: string;
}

export interface CwShiftReportDto {
  Shift: CwShiftDto;
  Cars: number;
  Revenue: number;
  AvgTicket: number;
  DiscountTotal: number;
  ByPayment: CwShiftPaymentStatDto[];
  ByWasher: { Name: string; Cars: number; Revenue: number }[];
  ByService: { Name: string; Count: number; Revenue: number }[];
}

/* ---- DTO → view model ---- */

export function toQueueCar(o: CwOrderDto): QueueCar {
  return {
    id: o.Id,
    plate: o.Plate,
    type: carTypeLabel(o.CarType),
    carTypeInt: o.CarType,
    services: o.Lines.map((l) => l.ServiceName),
    lines: o.Lines.map((l) => ({ name: l.ServiceName, price: l.Price })),
    subtotal: o.Subtotal,
    washers: (o.Washers ?? []).map((w) => ({ id: w.Id, name: w.Name })),
    washerNames: o.WasherName || '—',
    status: statusFromInt(o.Status),
    enteredAt: Date.parse(o.EnteredAt.endsWith('Z') ? o.EnteredAt : o.EnteredAt + 'Z'),
    customerName: o.CustomerName ?? null,
    customerVisits: o.CustomerVisits,
  };
}

/* ---- Endpoints ---- */

export const cwApi = {
  queue: async (): Promise<QueueCar[]> =>
    (await api.get<CwOrderDto[]>('/api/cw/order/queue')).map(toQueueCar),
  createOrder: (entry: IntakeEntry) =>
    api.post<CwOrderDto>('/api/cw/order', {
      Plate: entry.plate,
      CarType: entry.carType,
      WasherIds: entry.washerIds,
      ServiceIds: entry.serviceIds,
      StartWashing: entry.startWashing,
    }),
  advance: (id: string) => api.post<CwOrderDto>(`/api/cw/order/${id}/advance`),
  cancel: (id: string) => api.post<void>(`/api/cw/order/${id}/cancel`),
  pay: (id: string, req: CwPayRequest) => api.post<CwOrderDto>(`/api/cw/order/${id}/pay`, req),

  services: () => api.get<CwServiceDto[]>('/api/cw/service'),
  createService: (r: Omit<CwServiceDto, 'Id'>) => api.post<CwServiceDto>('/api/cw/service', r),
  updateService: (id: string, r: Omit<CwServiceDto, 'Id'>) =>
    api.put<CwServiceDto>(`/api/cw/service/${id}`, r),
  deleteService: (id: string) => api.del<void>(`/api/cw/service/${id}`),

  washers: () => api.get<CwWasherDto[]>('/api/cw/washer'),
  createWasher: (r: { Name: string; Phone?: string; IsActive: boolean }) =>
    api.post<CwWasherDto>('/api/cw/washer', r),
  updateWasher: (id: string, r: { Name: string; Phone?: string; IsActive: boolean }) =>
    api.put<CwWasherDto>(`/api/cw/washer/${id}`, r),
  deleteWasher: (id: string) => api.del<void>(`/api/cw/washer/${id}`),

  customers: () => api.get<CwCustomerDto[]>('/api/cw/customer'),
  customerByPlate: (plate: string) =>
    api.get<CwCustomerDto | null>(`/api/cw/customer/by-plate?plate=${encodeURIComponent(plate)}`),
  customerOrders: (id: string) =>
    api.get<CwCustomerOrderDto[]>(`/api/cw/customer/${id}/orders`),

  today: () => api.get<CwTodayStatsDto>('/api/cw/report/today'),
  report: () => api.get<CwReportDto>('/api/cw/report'),

  vehicleByPlate: (plate: string) =>
    api.get<CwVehicleInfoDto>(`/api/cw/vehicle?plate=${encodeURIComponent(plate)}`),

  qpayStatus: () => api.get<CwQpayStatusDto>('/api/cw/qpay/status'),
  qpayRequest: (bankAccountId: string) =>
    api.post<CwQpayStatusDto>('/api/cw/qpay/request', { BankAccountId: bankAccountId }),
  qpayStart: (orderId: string, req: { EbarimtType: number; EbarimtRd?: string | null }) =>
    api.post<CwQpayStartResponse>(`/api/cw/order/${orderId}/qpay/start`, req),
  qpayCheck: (orderId: string) =>
    api.post<CwQpayCheckResponse>(`/api/cw/order/${orderId}/qpay/check`),

  tenantPlan: (tenantId: string) => api.get<TenantPlanDto>(`/api/tenant/${tenantId}`),
  myInvoices: () => api.get<InvoiceDto[]>('/api/invoice/my'),
  createPlanInvoice: (tenantId: string, plan: number, periodMonths: number) =>
    api.post<InvoiceDto>('/api/invoice', { TenantId: tenantId, Plan: plan, PeriodMonths: periodMonths }),

  banks: () => api.get<BankDto[]>('/api/bank'),
  bankAccounts: () => api.get<BankAccountDto[]>('/api/bank-account'),
  /* tsetsu.mn-ийн банкны шалгуур (Голомт API) — данс заавал олдсон байх ёстой */
  lookupBankAccount: (req: { FKBankId: string; Value: string }) =>
    api.post<BankAccountLookupResponse>('/api/bank-account/lookup', req),
  createBankAccount: (req: { FKBankId: string; BankAccountNo: string; BankAccountName?: string; IsActive: boolean; IsDefault: boolean }) =>
    api.post<BankAccountDto>('/api/bank-account', req),

  shiftCurrent: () => api.get<CwShiftCurrentDto>('/api/cw/shift/current'),
  shiftOpen: () => api.post<CwShiftDto>('/api/cw/shift/open'),
  shiftClose: () => api.post<CwShiftReportDto>('/api/cw/shift/close'),
  shiftList: () => api.get<CwShiftDto[]>('/api/cw/shift'),
  shiftReport: (id: string) => api.get<CwShiftReportDto>(`/api/cw/shift/${id}/report`),
};
